<template>
  <div class="app-container">
    <el-row class="page-buttons">
      <editor-button action="cancel" style="float:right" @click="close" />
    </el-row>
    <div>
      <el-select
        v-model="selectedWorkshops"
        :multiple="true"
        class="search-select"
        type="select"
        style="width: 20%; margin-bottom: 10px"
        :placeholder="$t('common.workshop')"
        @input="handleWorkshopChange($event)"
      >
        <el-option v-for="workshop in workshops" :key="workshop.id" :label="workshop.name" :value="workshop.id" />
      </el-select>
      <el-select
        v-model="selectedDomainID"
        class="search-select"
        type="select"
        clearable
        style="margin-bottom: 10px; margin-left: 10px"
        :placeholder="$t('common.domain')"
        @change="handleDomainChange"
      >
        <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
      </el-select>
      <el-date-picker
        v-model="selectedYear"
        type="year"
        value-format="yyyy"
        :clearable="false"
        :placeholder="$t('common.year')"
        style="width: 10%; margin-bottom: 10px; margin-left: 10px"
        @change="handleYearChange"
      />
      <el-select
        v-model="selectedWeeks"
        :multiple="true"
        class="search-select"
        type="select"
        style="width: 20%; margin-bottom: 10px; margin-left: 10px"
        :placeholder="$t('componentList.weeks')"
        @input="handleWeekChange($event)"
      >
        <el-option-group v-if="availableWeeks.length">
          <el-option v-for="week in availableWeeks" :key="week" :label="week" :value="week" />
        </el-option-group>
      </el-select>
      <el-select
        v-model="selectedSeries"
        :multiple="true"
        :disabled="selectedWorkshops.length === 0 && selectedWeeks.length === 0"
        class="search-select"
        type="select"
        :placeholder="$t('series.series')"
        style="width: 25%; margin-bottom: 10px; margin-left: 10px"
      >
        <el-option-group v-if="availableSeries.length">
          <el-option v-for="series in availableSeries" :key="series.key" :label="series.value" :value="series.key" />
        </el-option-group>
      </el-select>
      <div style="float:right;">
        <editor-button
          :icon-only="false"
          :disabled="loading || (selectedWorkshops.length === 0 && selectedWeeks.length === 0)"
          button-translation="common.createReport"
          action="primary"
          @click="getComponentList()"
        />
        <editor-button
          :icon-only="false"
          :disabled="loading || (selectedWorkshops.length === 0 && selectedWeeks.length === 0)"
          button-translation="common.downloadExcel"
          action="primary"
          @click="downloadExcel()"
        />
      </div>
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
    </div>
    <ComponentListTotals :loading="loading" :series-product-types="seriesProductTypes" style="margin-bottom: 20px;" />
    <ComponentListExtras :loading="loading" :products="products" style="margin-bottom: 20px;" />
  </div>
</template>

<script>
import { ForceFileDownload } from '@/utils/general';
import EditorButton from '@/components/crud/EditorButton';
import { entityToStore } from '@/utils/store';
import { closeCurrentTag } from '@/utils/tags';
import { PlanningOption } from '@/utils/constants';
import ComponentListExtras from '@/pages/planning/series/ComponentListExtras';
import ComponentListTotals from '@/pages/planning/series/ComponentListTotals';

export default {
  name: 'StockCorrectionList',
  components: {
    ComponentListExtras,
    ComponentListTotals,
    EditorButton
  },
  props: {
    entity: {
      default: '',
      type: String
    },
    onEdit: {
      type: Function
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    permissionCode: String
  },
  data() {
    return {
      loading: false,
      entityStoreName: entityToStore(this.entity),
      errorMsg: '',
      loadedData: { items: [], total: 0 },
      total: 0,
      defaultCellAlign: 'left',
      items: [],
      products: [],
      seriesProductTypes: [],

      selectedWorkshops: [],

      selectedDomainID: this.getCurrentDomainID(),

      selectedYear: new Date().getFullYear().toString(),

      availableWeeks: [],
      selectedWeeks: [],

      availableSeries: [],
      selectedSeries: []
    };
  },
  computed: {
    domains() {
      return this.$store.getters.domains;
    },
    workshops() {
      return this.$store.getters.workshops;
    },
    listParams() {
      const selectedWorkshopsAsString = this.selectedWorkshops.join(',');
      const selectedWeeksAsString = this.selectedWeeks.join(',');
      const selectedSeriesAsString = this.selectedSeries.join(',');
      return {
        workshopIDs: selectedWorkshopsAsString,
        domainID: this.selectedDomainID,
        year: this.selectedYear,
        weeks: selectedWeeksAsString,
        series: selectedSeriesAsString
      };
    }
  },
  created() {
    this.getAvailableWeeks();
  },
  methods: {
    refreshList() {
      this.$store.dispatch('stockCorrection/readStocksIncludingNonExistent', {}).then(data => {
        this.loadedData.items = data.data.items;
        this.items = data.data.items;
        this.loadedData.total = data.data.total;
      });
    },
    close() {
      closeCurrentTag(this);
    },
    clearAllFilters() {
      this.$refs.stockCorrectionList.clearFilter();
      this.search = {};
    },
    getAvailableWeeks() {
      if (this.selectedWorkshops.length === 0) {
        this.availableWeeks = [];
        return;
      }
      const selectedWorkshopsAsString = this.selectedWorkshops.join(',');
      this.$store
        .dispatch('productionPlanningSeries/getAvailableWeeks', {
          workshopIDs: selectedWorkshopsAsString,
          domainID: this.selectedDomainID,
          year: this.selectedYear
        })
        .then(data => {
          this.availableWeeks = data.sort((a, b) => b - a); // Descending order
        });
    },
    getComponentList() {
      this.loading = true;
      this.$store
        .dispatch('productionPlanningSeries/getComponentList', this.listParams)
        .then(data => {
          this.products = data.seriesProducts;
          this.seriesProductTypes = data.seriesProductTypes;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCurrentDomainID() {
      const currentDomainID = this.$store.getters['domain/getCurrentDomainID'];
      return currentDomainID && currentDomainID > 0 ? currentDomainID : null;
    },
    handleWorkshopChange() {
      this.getAvailableWeeks();
      this.getAvailableSeries();
    },
    handleDomainChange(domain) {
      this.getAvailableWeeks();
      this.getAvailableSeries();
    },
    handleYearChange(year) {
      this.getAvailableWeeks();
    },
    handleWeekChange(week) {
      this.getAvailableSeries();
    },
    getAvailableSeries() {
      const selectedWorkshopsAsString = this.selectedWorkshops.join(',');
      const selectedWeeksAsString = this.selectedWeeks.join(',');
      this.$store
        .dispatch('productionPlanningSeries/getAvailableSeries', {
          workshopIDs: selectedWorkshopsAsString,
          domainID: this.selectedDomainID,
          year: this.selectedYear,
          weeks: selectedWeeksAsString
        })
        .then(data => {
          this.availableSeries = data;
        });
    },
    getUnit() {
      switch (this.planningOption) {
        case PlanningOption.Panel:
          return this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.panels').toLowerCase();
        case PlanningOption.NetCurtainRoomHigh:
        case PlanningOption.CurtainRoomHigh:
          return this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.curtainsMeters').toLowerCase();
        case PlanningOption.RomanBlind:
        case PlanningOption.Cutting:
          return this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.cuttings').toLowerCase();
      }
    },
    downloadExcel() {
      this.loading = true;
      this.$store
        .dispatch('productionPlanningSeries/downloadComponentList', this.listParams)
        .then(blob => {
          ForceFileDownload(blob, 'ComponentsList.xlsx');
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>
<style lang="scss">
.clear-input-button {
  float: right;
  background-color: #ff8c8c;
  color: white;

  &:hover {
    background-color: #ffb3b3;
    color: white;
  }
}
.validation-errors {
  display: block;
  color: #f56c6c;
  text-align: left;
  top: 100%;
}

.stock-correction-list {
  .edit-input {
    padding-right: 100px;
  }

  .cancel-btn {
    position: absolute;
    right: 15px;
    top: 10px;
  }

  .filter-cell > div {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .hide-overflow {
    .cell {
      white-space: nowrap;
    }
  }

  .search {
    .el-input__inner {
      padding-left: 25px;
      padding-right: 20px;
    }

    .el-input__prefix {
      left: 0;
    }

    .el-input__suffix {
      right: 0;
    }
  }

  .search-select {
    width: 100%;

    .el-input__inner {
      padding-left: 5px;
    }

    .el-input {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .el-input__suffix {
      top: -2px;
      right: 10px;
    }
  }

  .el-table .cell {
    min-height: 3em !important;
  }

  .el-table .cell {
    word-break: break-word;
  }

  .el-table .el-table__row {
    background-color: white;
  }

  .el-table tbody tr:hover .cell {
    color: initial;
  }

  .el-table .input-hidden {
    visibility: hidden;
  }
}
</style>
